import { StatusId, OperationBC, CommandeRang } from '../types';
import { commandeDuRang, commandePrecedente, estPremierRang } from './rangs';

// Logique PURE des boutons acheteur-BC : visibilite + desactivation metier de
// chaque action, par onglet (consultation / offre). Aucune dependance React,
// aucun concept MS/prestataire, aucun magic number : tout passe par StatusId.
// Les noms de statut peuvent changer en DB, les ids non -> on gate sur l'id.
//
// Partage des responsabilites avec le composant :
//   - ce module decide VISIBLE + DISABLED METIER (+ une raison pour le tooltip)
//   - les flags ephemeres (isSaving / isDeleting / loading / updatingReference)
//     restent dans la feuille : `disabled={action.disabled || isSaving}`.
//   - l'evenement runtime `chronoExpired` est injecte via inputs.chronoExpired ;
//     l'expiration par statut/date, elle, est calculee ici.

export interface ActionState {
  visible: boolean;
  disabled: boolean;
  reason?: string;
}

const EMPTY: ActionState = { visible: false, disabled: false };

// --- Helpers purs -------------------------------------------------------------
// Les notions de rang (commande d'un rang, rang precedent, premier rang) viennent de domain/rangs :
// une operation n'a pas forcement de rang 1, et le rang precedent n'est pas `rang - 1`.
const getCmd = commandeDuRang;

const isRefuseOuExpire = (idStatus?: number): boolean =>
  idStatus === StatusId.Refuse || idStatus === StatusId.Expire;

const cmdHasArticles = (cmd?: CommandeRang): boolean => (cmd?.articles?.length ?? 0) > 0;

const isBrouillon = (operation: OperationBC): boolean =>
  operation.status?.id === StatusId.BrouillonAcheteur;

const isTransferred = (operation: OperationBC): boolean => operation.transferred === true;

const isDateExpired = (dateStr?: string): boolean => {
  if (!dateStr) return false;
  const target = new Date(dateStr).getTime();
  return !Number.isNaN(target) && Date.now() >= target;
};

// Expiration telle que les boutons la voient : statut Expire/Annule, date depassee,
// ou evenement chrono (injecte). La feuille BDC est mono-source -> pas de garde `source`.
const isExpired = (operation: OperationBC, chronoExpired: boolean): boolean =>
  chronoExpired ||
  operation.status?.id === StatusId.Expire ||
  operation.status?.id === StatusId.Annule ||
  isDateExpired(operation.expire_at);

// Le prestataire a renvoye une offre exploitable : articles presents, ou statut
// AttenteAnalyse(13) / AAccepter(4).
const prestataireAEnvoyeOffre = (cmd?: CommandeRang): boolean =>
  !!cmd &&
  (cmdHasArticles(cmd) ||
    cmd.id_status === StatusId.AttenteAnalyse ||
    cmd.id_status === StatusId.AAccepter);

const cmdAvecProposition = (cmd?: CommandeRang): boolean =>
  cmd?.id_status === StatusId.PropositionFaite;

// Statuts ou "Annuler" n'a plus de sens (la commande est figee) : Brouillon(1),
// Valide(7), Refuse(8), Expire(9), Annule(14).
const isTerminalPourAnnulation = (idStatus?: number): boolean =>
  idStatus === StatusId.BrouillonAcheteur ||
  idStatus === StatusId.Valide ||
  idStatus === StatusId.Refuse ||
  idStatus === StatusId.Expire ||
  idStatus === StatusId.Annule;

// CGV deja acquises sans case a cocher : panier inverse (transfere), ou rang de repli
// debloque par le rang precedent refuse/expire en phase d'analyse.
const cgvDejaAcquises = (operation: OperationBC, rang: number): boolean => {
  if (isTransferred(operation)) return true;
  const cmdPrec = commandePrecedente(operation, rang);
  return operation.status?.id === StatusId.AAnalyser && isRefuseOuExpire(cmdPrec?.id_status);
};

// Porte de decision de l'onglet Offre : conditions sous lesquelles l'acheteur peut
// agir sur l'offre du prestataire (accepter / refuser).
const peutDeciderSurOffre = (operation: OperationBC, rang: number, expired: boolean): boolean => {
  if (expired) return false;
  const cmd = getCmd(operation, rang);
  const cmdPrec = commandePrecedente(operation, rang);
  const cmdRefuseOuExpire = isRefuseOuExpire(cmd?.id_status);

  if (isTransferred(operation)) {
    // Panier inverse : le prestataire a fait une proposition, statut AAccepter(4).
    return (
      operation.status?.id === StatusId.AAccepter &&
      !cmdRefuseOuExpire &&
      cmdAvecProposition(cmd)
    );
  }

  // Panier normal : phase AAnalyser(12).
  if (operation.status?.id === StatusId.AAnalyser) {
    if (estPremierRang(operation, rang)) {
      return !cmdRefuseOuExpire && prestataireAEnvoyeOffre(cmd);
    }
    return (
      isRefuseOuExpire(cmdPrec?.id_status) &&
      !!cmd &&
      cmd.id_status !== StatusId.BrouillonAcheteur &&
      !cmdRefuseOuExpire &&
      prestataireAEnvoyeOffre(cmd)
    );
  }

  return false;
};

// Edition de la consultation possible : brouillon acheteur et non expire. (Equivaut
// a `!isSectionReadOnly('admin')` cote feuille ; un transfert sort du statut brouillon.)
const consultationEditable = (operation: OperationBC, expired: boolean): boolean =>
  !expired && isBrouillon(operation);

// --- Onglet Consultation ------------------------------------------------------
export interface ConsultationInputs {
  isSignataire: boolean;
  // Voie de composition en cours : l'acheteur a clique « Je laisse le titulaire proposer un
  // chiffrage ». Seule voie qui change une regle de bouton (cf. `lancerConsultation`).
  modePrestataire: boolean;
  chronoExpired?: boolean;
}

export interface ConsultationActions {
  ajouterArticle: ActionState;
  choisirCatalogue: ActionState;
  laisserPrestataire: ActionState;
  changerMode: ActionState;
  sauvegarderQuitter: ActionState;
  lancerConsultation: ActionState;
  demanderValidation: ActionState;
  accepterOffre: ActionState; // decision panier inverse surfacee dans l'onglet consultation
  refuserOffre: ActionState;
  consulterRangSuivant: ActionState;
  annuler: ActionState;
}

export const computeConsultationActions = (
  operation: OperationBC,
  rang: number,
  inputs: ConsultationInputs,
): ConsultationActions => {
  const { isSignataire, modePrestataire, chronoExpired = false } = inputs;
  const expired = isExpired(operation, chronoExpired);
  const edit = consultationEditable(operation, expired); // !expired && brouillon acheteur
  const sid = operation.status?.id;
  const cmd = getCmd(operation, rang);
  const cmdPrec = commandePrecedente(operation, rang);
  // Zone de choix de composition ouverte : aucun article retenu, et l'acheteur n'est pas en train de
  // composer dans le catalogue.
  // `!== 'catalogue'` et NON `=== 'initial'` : le mode 'prestataire' en fait desormais partie. C'est
  // une intention LOCALE et revocable ("je laisse le titulaire proposer"), pas un etat qui ferme le
  // choix — le fermer faisait disparaitre la zone entiere (banniere, 3 voies et message compris) au
  // clic sur la 2e voie. Du temps ou ce clic transferait pour de bon, la faire disparaitre etait juste.

  // Decision panier inverse (offre du prestataire recue) : strictement AttenteAnalyse(13)
  // ou AAccepter(4) pour ce rang. Hors expiration.
  const offrePrestataireRecue =
    !expired &&
    isTransferred(operation) &&
    !!cmd &&
    (cmd.id_status === StatusId.AttenteAnalyse || cmd.id_status === StatusId.AAccepter);

  // Passage au rang suivant apres refus/expiration du precedent (dans l'ordre des rangs de
  // l'operation), rang encore en brouillon.
  const peutPasserRangSuivant =
    !expired &&
    isRefuseOuExpire(cmdPrec?.id_status) &&
    sid === StatusId.AAnalyser &&
    cmd?.id_status === StatusId.BrouillonAcheteur;

  // Le gate signataire du passage de rang suit le CHEMIN SERVEUR, pas le geste — les deux voies
  // n'ont pas la meme porte, et la feuille route deja dessus (`handleConfirmConsultationRangSuivant`
  // branche sur `operation.transferred`) :
  //   - operation TRANSFEREE -> POST transfertPrestataire, ouvert au role `acheteur` (le controleur
  //     de la passerelle branche explicitement sur `$isAcheteur`). Redemander un chiffrage au
  //     titulaire du rang suivant n'engage l'organisme sur AUCUN montant : c'est le meme
  //     raisonnement, et la meme route, que le lancement en mode « je laisse le titulaire proposer »
  //     (cf. `lancerConsultation` plus bas). La validation d'un signataire reste entiere plus loin,
  //     a l'acceptation de l'offre chiffree (`computeOffreActions.accepterOffre`).
  //   - panier NORMAL -> PUT consultation, qui renvoie 403 hors responsable_achat/validateur. Le
  //     signataire y reste donc obligatoire, sinon le bouton mene droit a une erreur.
  // Sans cette distinction, un acheteur simple dont le rang 1 refuse restait bloque sur « Obtenir la
  // validation de l'acheteur signataire » pour une action que le serveur lui ouvrait deja.
  const rangSuivantTransfere = peutPasserRangSuivant && isTransferred(operation);
  const rangSuivantAValider = peutPasserRangSuivant && !isTransferred(operation);

  // Plus de blocage lie au mode de composition : le mode prestataire ne bloque PLUS le lancement,
  // c'est justement lui qui transfere la demande au titulaire. "Lancer la consultation" est le seul
  // point d'engagement de l'operation, quel que soit le mode — l'ancienne regle (et son message
  // "la consultation se lance apres reception des articles") datait du temps ou le transfert avait son
  // propre bouton et sa propre confirmation.

  // Lancer en mode "je laisse le titulaire proposer" n'engage l'organisme sur AUCUN montant : ca
  // demande un chiffrage au titulaire du rang, qui revient ensuite sur le compte de l'acheteur. Un
  // acheteur simple peut donc le declencher seul ; la validation d'un signataire reste entiere plus
  // loin, a l'acceptation de l'offre chiffree (`computeOffreActions.accepterOffre`).
  // Les deux chemins sont deja distincts cote serveur : `transfertPrestataire` accepte le role
  // `acheteur`, tandis que `lancerConsultation` et `acceptAcheteur` renvoient 403 hors
  // responsable_achat/validateur. Cette regle n'ouvre donc rien : elle aligne l'UI sur ce que le
  // serveur autorise deja, la ou le bouton unique bloquait l'acheteur sur une action permise.

  // "Obtenir la validation" (non-signataire) apparait en mode edition (lancement) ET
  // au passage de rang — sauf quand l'action elle-meme ne requiert pas de signataire. Le laisser
  // cohabiter avec "Consulter le prestataire de rang N" renverrait l'acheteur sur le chemin qui
  // le bloquait.
  const demanderValidationVisible =
    ((edit && !modePrestataire) || rangSuivantAValider) && !isSignataire;

  return {
    ajouterArticle: { visible: edit, disabled: false },
    // Les 3 voies de composition sont offertes tant que la consultation est EDITABLE, y compris
    // lorsqu'un panier est deja compose : c'est par elles qu'on change d'avis depuis la suppression
    // du bouton « Changer de mode ». Elles etaient conditionnees a `modeSansSelection`, donc elles
    // disparaissaient au premier article ajoute — l'acheteur se retrouvait sans porte de sortie.
    choisirCatalogue: { visible: edit, disabled: false },
    laisserPrestataire: { visible: edit, disabled: false },
    changerMode: { visible: edit, disabled: false },
    sauvegarderQuitter: { visible: edit, disabled: false },
    lancerConsultation: {
      visible: edit && (isSignataire || modePrestataire),
      disabled: false,
    },
    demanderValidation: {
      visible: demanderValidationVisible,
      disabled: false,
    },
    accepterOffre: { visible: offrePrestataireRecue, disabled: false },
    refuserOffre: { visible: offrePrestataireRecue, disabled: false },
    consulterRangSuivant: {
      visible: rangSuivantTransfere || (rangSuivantAValider && isSignataire),
      disabled: false,
    },
    annuler: {
      // Tous les contextes "Annuler" (edition, panier inverse, passage de rang, repli
      // non terminal), masque si expire.
      visible:
        !expired &&
        (edit || offrePrestataireRecue || peutPasserRangSuivant || !isTerminalPourAnnulation(sid)),
      disabled: false,
    },
  };
};

// --- Onglet Offre -------------------------------------------------------------
// Les CGV ne desactivent PAS le bouton "Accepter" : elles sont une etape du flux
// (clic -> popup CGV -> signature), pilotee dans la feuille. Le domaine ne decide
// donc que la visibilite ; le bouton n'est bloque que par le `loading` ephemere.
export interface OffreInputs {
  isSignataire: boolean;
  chronoExpired?: boolean;
}

export interface OffreActions {
  accepterOffre: ActionState;
  refuserOffre: ActionState;
  demanderValidation: ActionState;
  annuler: ActionState;
  telechargerBdc: ActionState;
  modifierReference: ActionState;
}

export const computeOffreActions = (
  operation: OperationBC,
  rang: number,
  inputs: OffreInputs,
): OffreActions => {
  const { isSignataire, chronoExpired = false } = inputs;
  const expired = isExpired(operation, chronoExpired);
  const sid = operation.status?.id;
  const cmd = getCmd(operation, rang);
  const decision = peutDeciderSurOffre(operation, rang, expired);

  return {
    accepterOffre: {
      visible: decision && isSignataire,
      disabled: false,
    },
    refuserOffre: { visible: decision, disabled: false },
    demanderValidation: { visible: decision && !isSignataire, disabled: false },
    annuler: {
      // Decision en cours, ou repli "non terminal" hors fenetre de decision.
      visible: decision || (!expired && !isTerminalPourAnnulation(sid)),
      disabled: false,
    },
    telechargerBdc: { visible: cmd?.id_status === StatusId.Valide, disabled: false },
    modifierReference: {
      visible:
        isTransferred(operation) &&
        operation.status?.id === StatusId.AAccepter &&
        !isRefuseOuExpire(cmd?.id_status) &&
        cmdAvecProposition(cmd),
      disabled: false,
    },
  };
};

// Expose pour reutilisation/tests cibles (memes regles que ci-dessus).
export const operationActionsInternals = {
  isExpired,
  // Expose : c'est LA definition maison du "rang ecarte de la cascade" (refuse OU expire), deja
  // utilisee par toutes les regles de boutons ci-dessus. L'onglet Notification en a besoin pour lister
  // les non-retenus ; en reecrire une seconde ailleurs les ferait diverger.
  isRefuseOuExpire,
  peutDeciderSurOffre,
  cgvDejaAcquises,
  prestataireAEnvoyeOffre,
  isTerminalPourAnnulation,
  EMPTY,
};
